import { customerContactsQuery, customerQuery } from "@common/api/customers"
import type { CustomerContactPublic } from "@common/client"
import { InlineEmpty } from "@common/components/shared/inline-empty"
import { Badge } from "@common/components/ui/badge"
import { Button } from "@common/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@common/components/ui/table"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { z } from "zod"
import { ListTabs } from "@/components/layout/list-tabs"
import { SubHeader } from "@/components/page/section"
import { DETAIL_TABLE } from "@/routes/_layout/customer_/$customerId/-components/detail-table"

const CONTACT_LIMIT = 100

function contactsParams(
  customerId: string,
  archived: boolean | undefined,
): { customer_id: string; limit: number; archived?: boolean } {
  return {
    customer_id: customerId,
    limit: CONTACT_LIMIT,
    ...(archived && { archived: true }),
  }
}

export const Route = createFileRoute(
  "/_layout/customer_/$customerId/contacts/_list",
)({
  validateSearch: z.object({ archived: z.boolean().optional().catch(false) })
    .parse,
  loaderDeps: ({ search }) => ({ archived: search.archived }),
  loader: ({ context: { queryClient }, params: { customerId }, deps }) =>
    queryClient.ensureQueryData(
      customerContactsQuery(contactsParams(customerId, deps.archived)),
    ),
  component: CustomerContacts,
})

function CustomerContacts(): React.ReactElement {
  return (
    <div className="flex min-h-0 min-w-0 flex-1">
      <div className="min-w-0 flex-1 overflow-y-auto bg-background pb-6">
        <Header />
        <ContactTable />
      </div>
      <Outlet />
    </div>
  )
}

function Header(): React.ReactElement {
  const { customerId } = Route.useParams()
  const { archived = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: customer } = useSuspenseQuery(
    customerQuery({ customer_id: customerId }),
  )
  const canCreate =
    useHasPermission("customer_contact:create") && !customer.archived_at

  return (
    <SubHeader
      title="Contacts"
      tabs={
        <ListTabs
          tabs={["active", "archived"] as const}
          tab={archived ? "archived" : "active"}
          label={(tab) => (tab === "active" ? "Active" : "Archived")}
          name="Record status"
          onSelect={(tab) =>
            navigate({
              search: (previous) => ({
                ...previous,
                archived: tab === "archived" || undefined,
              }),
              replace: true,
            })
          }
        />
      }
      action={
        canCreate && (
          <Button asChild size="sm">
            <Link
              to="/customer/$customerId/contacts/create"
              params={{ customerId }}
            >
              New contact
            </Link>
          </Button>
        )
      }
    />
  )
}

function ContactTable(): React.ReactElement {
  const { customerId } = Route.useParams()
  const { archived = false } = Route.useSearch()
  const { data } = useSuspenseQuery(
    customerContactsQuery(contactsParams(customerId, archived)),
  )
  const contacts = data.data

  if (contacts.length === 0) {
    return (
      <InlineEmpty>
        {archived
          ? "No archived contacts for this customer."
          : "No contacts for this customer."}
      </InlineEmpty>
    )
  }

  return (
    <Table className={DETAIL_TABLE}>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Job title</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Phone</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contacts.map((contact) => (
          <ContactRow key={contact.id} contact={contact} />
        ))}
      </TableBody>
    </Table>
  )
}

function ContactRow({
  contact,
}: {
  contact: CustomerContactPublic
}): React.ReactElement {
  const navigate = useNavigate()

  return (
    <TableRow
      className="cursor-pointer border-b-0"
      onClick={() =>
        navigate({
          to: "/customer/$customerId/contacts/$contactId",
          params: { customerId: contact.customer_id, contactId: contact.id },
          search: true,
        })
      }
    >
      <TableCell>
        <span className="flex items-center gap-2">
          <Link
            to="/customer/$customerId/contacts/$contactId"
            params={{ customerId: contact.customer_id, contactId: contact.id }}
            search={true}
            onClick={(event) => event.stopPropagation()}
            className="text-foreground hover:no-underline"
          >
            {contact.first_name} {contact.last_name}
          </Link>
          {contact.is_primary && !contact.archived_at && (
            <Badge variant="secondary" className="text-small-regular">
              Primary
            </Badge>
          )}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {contact.job_title}
      </TableCell>
      <TableCell className="text-muted-foreground">{contact.email}</TableCell>
      <TableCell className="text-muted-foreground">{contact.phone}</TableCell>
    </TableRow>
  )
}
